// The CanonicalRoom → Snapshot bridge: a jam room's canonical state rendered
// as the same artefacts an extension upload produces — an arrangement
// Snapshot whose clips carry their own audio objects. Moved here from
// viz/worker/publish.ts so the core API, the viz worker, and the expiry
// sweep all share ONE copy (pure functions only; callers do the I/O).
//
// Two properties matter more than anything else here:
//
//  1. POOL AUDIO IS REFERENCED; LEGACY AUDIO IS COPIED. A clip in the shared
//     blob pool (`blobs/<hash>.wav`, shared/blobs.ts) is one object for every
//     seed, version and jam that holds those bytes, kept alive by a reference
//     row per holder — so a publish takes a reference for the new seed and
//     stamps the SAME key, moving no bytes. Legacy keys are different: room
//     clips may point at the ephemeral `jam/<jamId>/…` prefix, deleted when
//     the room expires (workers/src/jam/room.ts /expire), and a published seed
//     referencing those would go silent the day its room is cleaned up — so
//     every distinct legacy object is still copied into the seed's own
//     `<user>/<newSeedId>/clips/` prefix.
//
//  2. THE PUBLISH IS HONEST ABOUT MISSING AUDIO. A clip whose object is gone
//     from R2 (or whose pool row was swept) is published with `audioKey: null`
//     (rendered, listed, unplayable — the same degradation the viz already
//     shows) rather than failing the whole publish or, worse, referencing
//     bytes that don't exist.

import { readyRender } from "./sync/clip-render.js";
import { isPoolKey } from "./blobs.js";
import { notesDigest } from "./midi/notes.js";
import type { RoomStateResponse } from "./sync/intents.js";
import { cloneDevices, type DeviceState } from "./sync/state.js";
import type {
  AudioClipInfo,
  AudioTrackInfo,
  MidiClipInfo,
  MidiTrackInfo,
  Snapshot,
  TrackInfoBase,
} from "./seed-history.js";

type RoomTrack = RoomStateResponse["tracks"][number];
type RoomClip = RoomStateResponse["clips"][number];

/** Every audio object a room's clips reference: each clip's own, and the
 * render of its warp when it has one — both travel into the seed. */
export function audioObjectsOf(clips: ReadonlyArray<Pick<RoomClip, "audioKey"> & { render?: { key?: string } }>): Array<{ audioKey: string | null }> {
  return clips.flatMap((c) => (c.render?.key ? [{ audioKey: c.audioKey }, { audioKey: c.render.key }] : [{ audioKey: c.audioKey }]));
}

/**
 * One R2 copy per DISTINCT LEGACY source object — content addressing means a
 * loop placed forty times is one object, and it stays one object in the seed.
 * Pool keys (`blobs/…` in this channel) are excluded: they are referenced,
 * not copied (see the header). Returns sourceKey → destKey. Dest basenames
 * that collide after slugging are disambiguated with a numeric suffix so two
 * different objects can never be written to one key.
 *
 * `channelPrefix` is the caller's channel ("" stable, "beta/" beta) and is
 * stamped onto every destination key. It used to be derived from the source
 * key by finding "jam/" in it — which breaks the moment a room holds clips
 * referencing a seed's own `<user>/<seed>/clips/` prefix (hydrated rooms do),
 * silently planning a beta copy onto an unprefixed stable key. The caller
 * always knows its channel; the key does not.
 */
export function planAudioCopies(
  clips: ReadonlyArray<Pick<RoomClip, "audioKey"> & { render?: { key?: string } }>,
  ownerId: string,
  newSeedId: string,
  channelPrefix: string,
): Map<string, string> {
  const plan = new Map<string, string>();
  const taken = new Set<string>();
  for (const clip of audioObjectsOf(clips)) {
    const source = clip.audioKey;
    if (!source || plan.has(source) || isPoolKey(source, channelPrefix)) continue;
    const base = source.split("/").pop() ?? "clip";
    const dot = base.lastIndexOf(".");
    const stem = slugFile(dot > 0 ? base.slice(0, dot) : base);
    const ext = dot > 0 ? base.slice(dot).toLowerCase() : ".wav";
    let name = `${stem}${ext}`;
    for (let n = 2; taken.has(name); n++) name = `${stem}-${n}${ext}`;
    taken.add(name);
    plan.set(source, `${channelPrefix}${ownerId}/${newSeedId}/clips/${name}`);
  }
  return plan;
}

/**
 * The room's canonical state as an upload-shaped arrangement Snapshot.
 *
 * The room genuinely does not know some things a Live capture would (grid,
 * scale, cue points) — those are carried over from the ORIGIN
 * seed's arrangement where a track of the same name exists there, and fall
 * back to neutral defaults otherwise. What the room does know — tracks, clip
 * geometry, warp fields, mute states, mixer volume and pan, audio identity —
 * is authoritative and comes from the room alone. (Volume and pan fall back
 * to the origin/neutral chain only for rooms from pre-mixer-sync servers.)
 *
 * `destBySource` maps a clip's room audioKey to its copied seed key, or null
 * when the source object was missing; clips with no entry publish without
 * audio rather than referencing an object that is about to be deleted with
 * the room.
 */
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function buildArrangement(
  room: {
    tracks: ReadonlyArray<RoomTrack>;
    clips: ReadonlyArray<RoomClip>;
    sceneNames?: ReadonlyArray<string | null>;
    cuePoints?: ReadonlyArray<{ name: string; time: number }>;
    cuePointsRev?: number;
    master?: { volume: number; pan: number };
    masterRev?: number;
    returns?: ReadonlyArray<{ name: string; mute: boolean; solo: boolean; volume: number; pan: number }>;
    returnsRev?: number;
    chains?: Record<string, { devices: readonly DeviceState[]; rev: number }>;
    scale?: { rootNote: number; name: string; mode: boolean; intervals: readonly number[] };
    scaleRev?: number;
  },
  origin: Snapshot | null,
  tempo: number,
  destBySource: ReadonlyMap<string, string | null>,
): Snapshot {
  const originByName = new Map<string, TrackInfoBase>();
  for (const t of origin?.audioTracks ?? []) originByName.set(t.name, t);
  for (const t of origin?.midiTracks ?? []) originByName.set(`midi|${t.name}`, t);
  const originMidiByName = new Map<string, MidiTrackInfo>();
  for (const t of origin?.midiTracks ?? []) originMidiByName.set(t.name, t);

  const tracks = [...room.tracks].sort((a, b) => a.ord - b.ord || a.id.localeCompare(b.id));
  // Timeline clips and Session View clips are one flat list in the room and
  // two collections on a Snapshot (a slot clip has no timeline position).
  const clipsOf = (t: RoomTrack): RoomClip[] =>
    room.clips
      .filter((c) => c.trackId === t.id && c.slot === undefined)
      .sort((a, b) => a.startTime - b.startTime || a.id.localeCompare(b.id));
  const sessionClipsOf = (t: RoomTrack): Array<RoomClip & { slot: number }> =>
    room.clips
      .filter((c): c is RoomClip & { slot: number } => c.trackId === t.id && c.slot !== undefined)
      .sort((a, b) => a.slot - b.slot || a.id.localeCompare(b.id));
  const base = (t: RoomTrack, prior: TrackInfoBase | undefined): TrackInfoBase => ({
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    mutedViaSolo: false,
    volume: t.volume ?? prior?.volume ?? 0.85,
    panning: t.pan ?? prior?.panning ?? 0,
    groupTrackName: prior?.groupTrackName ?? null,
    ...(t.sends && t.sends.length > 0 ? { sends: [...t.sends] } : prior?.sends ? { sends: [...prior.sends] } : {}),
    ...(t.devices && t.devices.length > 0
      ? { devices: cloneDevices(t.devices) }
      : prior?.devices
        ? { devices: cloneDevices(prior.devices) }
        : {}),
  });

  const audioTracks: AudioTrackInfo[] = tracks
    .filter((t) => t.trackKind !== "midi")
    .map((t) => {
      const session = sessionClipsOf(t);
      return {
        ...base(t, originByName.get(t.name)),
        arrangementClips: clipsOf(t).map((c) => toClipInfo(c, destBySource)),
        takeLanes: [],
        ...(session.length > 0
          ? {
              sessionClips: session.map((c) => ({
                ...toClipInfo(c, destBySource),
                slot: c.slot,
                ...(c.warpMarkers && c.warpMarkers.length >= 2 ? { warpMarkers: c.warpMarkers.map((m) => ({ beat: m.beat, sec: m.sec })) } : {}),
              })),
            }
          : {}),
      };
    });
  const midiTracks: MidiTrackInfo[] = tracks
    .filter((t) => t.trackKind === "midi")
    .map((t) => {
      const session = sessionClipsOf(t);
      // The room's instrument, else the origin's lane of the same name — a
      // commit made from Live carries none, and must not reset it to sine.
      const instrument = t.instrument ?? originMidiByName.get(t.name)?.instrument;
      return {
        ...base(t, originByName.get(`midi|${t.name}`)),
        kind: "midi" as const,
        ...(instrument ? { instrument } : {}),
        arrangementClips: clipsOf(t).map((c) => toMidiClipInfo(c, destBySource)),
        ...(session.length > 0
          ? { sessionClips: session.map((c) => ({ ...toMidiClipInfo(c, destBySource), slot: c.slot })) }
          : {}),
      };
    });

  const clipCount = audioTracks.reduce((n, t) => n + t.arrangementClips.length, 0);
  const midiClipCount = midiTracks.reduce((n, t) => n + t.arrangementClips.length, 0);
  const sessionClipCount =
    audioTracks.reduce((n, t) => n + (t.sessionClips?.length ?? 0), 0) +
    midiTracks.reduce((n, t) => n + (t.sessionClips?.length ?? 0), 0);
  // Scene rows: as many as the room's highest slot needs, its own scene names
  // where it holds them (the room is the truth once hydrated), the origin's
  // where it doesn't, unnamed padding for the rest. Scene tempos are the
  // origin's — the room never carries them.
  const highestSlot = room.clips.reduce((m, c) => (c.slot !== undefined ? Math.max(m, c.slot) : m), -1);
  const originScenes = origin?.song.scenes ?? [];
  const roomScenes = room.sceneNames ?? [];
  const sceneCount = Math.max(originScenes.length, roomScenes.length, highestSlot + 1);
  const scenes =
    sceneCount <= 0
      ? undefined
      : Array.from({ length: sceneCount }, (_, i) => ({
          name: roomScenes[i] ?? originScenes[i]?.name ?? "",
          tempo: originScenes[i]?.tempo ?? -1,
        }));
  // Locators: the room's once asserted (rev > 0 — the jam moved or named
  // them), else the origin's.
  const cuePoints =
    (room.cuePointsRev ?? 0) > 0
      ? (room.cuePoints ?? []).map((c) => ({ name: c.name, time: c.time }))
      : (origin?.song.cuePoints ?? []);
  // The master fader: the room's once asserted, else the origin's.
  const master =
    (room.masterRev ?? 0) > 0 && room.master
      ? { volume: room.master.volume, panning: room.master.pan }
      : origin?.song.master;
  const chains = room.chains ?? {};
  const returns = (
    (room.returnsRev ?? 0) > 0 && room.returns
      ? room.returns.map((r) => ({ name: r.name, mute: r.mute, solo: r.solo, volume: r.volume, panning: r.pan }))
      : origin?.song.returns
  )?.map((r, i) => {
    const chain = chains[`return:${i}`];
    const prior = origin?.song.returns?.[i]?.devices;
    return chain && chain.rev > 0 ? { ...r, devices: cloneDevices(chain.devices) } : prior ? { ...r, devices: cloneDevices(prior) } : r;
  });
  const masterChain = chains["master"];
  const masterDevices = masterChain && masterChain.rev > 0 ? cloneDevices(masterChain.devices) : origin?.song.masterDevices;
  // The scale: the room's once a Set asserted it, else the origin's.
  const scale =
    (room.scaleRev ?? 0) > 0 && room.scale
      ? { rootNote: room.scale.rootNote, rootNoteName: NOTE_NAMES[room.scale.rootNote] ?? "C", name: room.scale.name, mode: room.scale.mode, intervals: [...room.scale.intervals] }
      : undefined;
  return {
    song: origin
      ? { ...origin.song, tempo, cuePoints, ...(scenes ? { scenes } : {}), ...(master ? { master } : {}), ...(returns ? { returns } : {}), ...(masterDevices ? { masterDevices } : {}), ...(scale ? { scale } : {}) }
      : {
          tempo,
          gridQuantization: { value: 0, label: "unknown", isTriplet: false },
          scale: scale ?? { rootNote: 0, rootNoteName: "C", name: "Major", mode: false, intervals: [] },
          cuePoints,
          ...(scenes ? { scenes } : {}),
          ...(master ? { master } : {}),
          ...(returns ? { returns } : {}),
          ...(masterDevices ? { masterDevices } : {}),
        },
    audioTracks,
    ...(midiTracks.length > 0 ? { midiTracks } : {}),
    totals: {
      audioTrackCount: audioTracks.length,
      arrangementClipCount: clipCount,
      takeLaneClipCount: 0,
      ...(midiTracks.length > 0 ? { midiTrackCount: midiTracks.length, midiClipCount } : {}),
      ...(sessionClipCount > 0 ? { sessionClipCount } : {}),
    },
  };
}

function toMidiClipInfo(c: RoomClip, destBySource: ReadonlyMap<string, string | null>): MidiClipInfo {
  const notes = c.notes ?? [];
  return {
    name: c.name,
    startTime: c.startTime,
    endTime: c.startTime + c.duration,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    color: c.color,
    colorHex: colorHex(c.color),
    muted: c.muted,
    notes,
    noteCount: notes.length,
    notesDigest: c.notesDigest ?? notesDigest(notes),
    audioKey: c.audioKey ? (destBySource.get(c.audioKey) ?? null) : null,
  };
}

function toClipInfo(c: RoomClip, destBySource: ReadonlyMap<string, string | null>): AudioClipInfo {
  return {
    name: c.name,
    startTime: c.startTime,
    endTime: c.startTime + c.duration,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    color: c.color,
    colorHex: colorHex(c.color),
    muted: c.muted,
    filePath: c.filePath,
    warping: c.isWarped,
    warpMode: { value: c.warpModeValue, name: WARP_MODE_NAMES[c.warpModeValue] ?? "" },
    warpMarkerCount: 0,
    audioKey: c.audioKey ? (destBySource.get(c.audioKey) ?? null) : null,
    ...(c.libraryClip ? { libraryClip: { id: c.libraryClip.id, ownerId: c.libraryClip.ownerId } } : {}),
    // A room's clip points at the audio it windows into, not at a render of
    // it: say so, and carry the settings that window and shape it, so a
    // reader rebuilds the clip rather than playing the source flat.
    audioIs: "source",
    // The browser's warp as a render, when one is READY (made from exactly
    // this clip) and its object made the trip: hosts without a warp engine
    // read the clip through it (clip-render.ts `withRenders`).
    ...(readyRender(c) && destBySource.get(readyRender(c)!.key)
      ? { render: { key: destBySource.get(readyRender(c)!.key)!, tempo: readyRender(c)!.tempo } }
      : {}),
    ...(c.sourceTempo !== undefined ? { sourceTempo: c.sourceTempo } : {}),
    ...(c.warpMarkers && c.warpMarkers.length >= 2 ? { warpMarkers: c.warpMarkers.map((m) => ({ beat: m.beat, sec: m.sec })) } : {}),
    ...(c.transpose ? { transpose: c.transpose } : {}),
    ...(c.detune ? { detune: c.detune } : {}),
    ...(c.warpSettings && Object.keys(c.warpSettings).length > 0 ? { warpSettings: { ...c.warpSettings } } : {}),
  };
}

/** Live's warp modes by parameter value; unknown values publish with no name. */
const WARP_MODE_NAMES: Record<number, string> = {
  0: "Beats",
  1: "Tones",
  2: "Texture",
  3: "Re-Pitch",
  4: "Complex",
  5: "REX",
  6: "Complex Pro",
};

function colorHex(packed: number): string {
  return `#${(packed & 0xffffff).toString(16).padStart(6, "0")}`;
}

function slugFile(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "clip"
  );
}
